import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { useForm } from 'react-hook-form';
import {
  Button,
  Input,
  Label,
  Textarea,
  cn,
  Badge,
  Switch,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@pantry/ui';
import {
  useCreateAnnouncement,
  useUpdateAnnouncement,
  useDeleteAnnouncement,
  useAnnouncementStats,
  type Announcement,
  type CreateAnnouncementDto,
  ANNOUNCEMENT_SEVERITIES,
  ANNOUNCEMENT_PLATFORMS,
  SEVERITY_LABELS,
  STATUS_LABELS,
  PLATFORM_LABELS,
  type AnnouncementSeverity,
  type AnnouncementPlatform,
} from '@pantry/api-client';
import {
  XMarkIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  BellAlertIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';

// Severity icons
const SEVERITY_ICONS: Record<AnnouncementSeverity, React.ReactNode> = {
  info: <InformationCircleIcon className="h-5 w-5" />,
  warning: <ExclamationTriangleIcon className="h-5 w-5" />,
  critical: <BellAlertIcon className="h-5 w-5" />,
};

// Severity colors for preview
const SEVERITY_PREVIEW_COLORS: Record<AnnouncementSeverity, string> = {
  info: 'bg-blue-50 border-blue-200 text-blue-800',
  warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
  critical: 'bg-red-50 border-red-200 text-red-800',
};

interface FormData {
  severity: AnnouncementSeverity;
  title: string;
  message: string;
  linkUrl: string;
  linkText: string;
  activeFrom: string;
  activeUntil: string;
  dismissible: boolean;
  targetPlatforms: AnnouncementPlatform[];
}

interface AnnouncementFormSheetProps {
  announcement: Announcement | null;
  onClose: () => void;
}

export function AnnouncementFormSheet({ announcement, onClose }: AnnouncementFormSheetProps) {
  const { t } = useLingui();
  const isEditing = !!announcement;
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const createMutation = useCreateAnnouncement();
  const updateMutation = useUpdateAnnouncement();
  const deleteMutation = useDeleteAnnouncement();

  // Fetch stats if editing
  const { data: stats } = useAnnouncementStats(announcement?.id || '', {
    enabled: isEditing,
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    defaultValues: {
      severity: announcement?.severity || 'info',
      title: announcement?.title || '',
      message: announcement?.message || '',
      linkUrl: announcement?.linkUrl || '',
      linkText: announcement?.linkText || '',
      activeFrom: announcement?.activeFrom
        ? new Date(announcement.activeFrom).toISOString().slice(0, 16)
        : '',
      activeUntil: announcement?.activeUntil
        ? new Date(announcement.activeUntil).toISOString().slice(0, 16)
        : '',
      dismissible: announcement?.dismissible ?? true,
      targetPlatforms: (announcement?.targetPlatforms as AnnouncementPlatform[]) || [],
    },
  });

  const watchedValues = watch();

  const onSubmit = async (data: FormData) => {
    try {
      const payload: CreateAnnouncementDto = {
        severity: data.severity,
        title: data.title,
        message: data.message || undefined,
        linkUrl: data.linkUrl || undefined,
        linkText: data.linkText || undefined,
        activeFrom: data.activeFrom || undefined,
        activeUntil: data.activeUntil || undefined,
        dismissible: data.dismissible,
        targetPlatforms: data.targetPlatforms.length > 0 ? data.targetPlatforms : undefined,
      };

      if (isEditing) {
        await updateMutation.mutateAsync({ id: announcement.id, data: payload });
        toast.success(t`Announcement updated`);
      } else {
        await createMutation.mutateAsync(payload);
        toast.success(t`Announcement created`);
      }
      onClose();
    } catch {
      toast.error(isEditing ? t`Failed to update announcement` : t`Failed to create announcement`);
    }
  };

  const handleDelete = async () => {
    if (!announcement) return;
    try {
      await deleteMutation.mutateAsync(announcement.id);
      toast.success(t`Announcement deleted`);
      onClose();
    } catch {
      toast.error(t`Failed to delete announcement`);
    }
  };

  const togglePlatform = (platform: AnnouncementPlatform) => {
    const current = watchedValues.targetPlatforms || [];
    if (current.includes(platform)) {
      setValue(
        'targetPlatforms',
        current.filter((p) => p !== platform),
        { shouldDirty: true }
      );
    } else {
      setValue('targetPlatforms', [...current, platform], { shouldDirty: true });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose}>
        <div
          className="fixed right-0 top-0 h-full w-full max-w-lg bg-card border-l border-border shadow-xl animate-in slide-in-from-right duration-200 overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
            <div>
              <h2 className="text-lg font-semibold">
                {isEditing ? <Trans>Edit Announcement</Trans> : <Trans>New Announcement</Trans>}
              </h2>
              {isEditing && announcement && (
                <Badge
                  variant="secondary"
                  className={cn(
                    'mt-1',
                    announcement.status === 'active' && 'bg-green-100 text-green-700',
                    announcement.status === 'scheduled' && 'bg-purple-100 text-purple-700',
                    announcement.status === 'draft' && 'bg-zinc-100 text-zinc-700',
                    announcement.status === 'expired' && 'bg-zinc-100 text-zinc-500'
                  )}
                >
                  {STATUS_LABELS[announcement.status]}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPreview(!showPreview)}
                className="rounded-lg gap-1.5"
              >
                <EyeIcon className="h-4 w-4" />
                <Trans>Preview</Trans>
              </Button>
              <Button variant="ghost" size="icon" onClick={onClose} className="rounded-lg">
                <XMarkIcon className="h-5 w-5" />
                <span className="sr-only">
                  <Trans>Close</Trans>
                </span>
              </Button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
            {/* Preview */}
            {showPreview && (
              <div
                className={cn(
                  'rounded-lg border p-4',
                  SEVERITY_PREVIEW_COLORS[watchedValues.severity]
                )}
              >
                <div className="flex items-start gap-3">
                  <span className="mt-0.5">{SEVERITY_ICONS[watchedValues.severity]}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{watchedValues.title || t`Announcement title`}</p>
                    {watchedValues.message && (
                      <p className="text-sm mt-1 opacity-90">{watchedValues.message}</p>
                    )}
                    {watchedValues.linkUrl && (
                      <a
                        href={watchedValues.linkUrl}
                        className="text-sm font-medium underline mt-2 inline-block"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {watchedValues.linkText || t`Learn more`}
                      </a>
                    )}
                  </div>
                  {watchedValues.dismissible && (
                    <button type="button" className="opacity-50 hover:opacity-100">
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Severity */}
            <div className="space-y-2">
              <Label>
                <Trans>Severity</Trans>
              </Label>
              <div className="flex gap-2">
                {ANNOUNCEMENT_SEVERITIES.map((severity) => (
                  <button
                    key={severity}
                    type="button"
                    onClick={() => setValue('severity', severity, { shouldDirty: true })}
                    className={cn(
                      'flex-1 flex items-center justify-center gap-2 rounded-lg border py-2.5 px-3 text-sm font-medium transition-colors',
                      watchedValues.severity === severity
                        ? severity === 'info'
                          ? 'bg-blue-50 border-blue-300 text-blue-700'
                          : severity === 'warning'
                            ? 'bg-yellow-50 border-yellow-300 text-yellow-700'
                            : 'bg-red-50 border-red-300 text-red-700'
                        : 'bg-muted/30 border-border hover:bg-muted/50'
                    )}
                  >
                    {SEVERITY_ICONS[severity]}
                    {SEVERITY_LABELS[severity]}
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">
                <Trans>Title *</Trans>
              </Label>
              <Input
                id="title"
                {...register('title', { required: t`Title is required`, maxLength: 200 })}
                placeholder={t`Enter announcement title`}
                className={cn(errors.title && 'border-destructive')}
              />
              {errors.title && <p className="text-sm text-destructive">{errors.title.message}</p>}
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="message">
                <Trans>Message</Trans>
              </Label>
              <Textarea
                id="message"
                {...register('message', { maxLength: 1000 })}
                placeholder={t`Optional description`}
                rows={3}
              />
            </div>

            {/* Link */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="linkUrl">
                  <Trans>Link URL</Trans>
                </Label>
                <Input id="linkUrl" type="url" {...register('linkUrl')} placeholder="https://..." />
              </div>
              <div className="space-y-2">
                <Label htmlFor="linkText">
                  <Trans>Link Text</Trans>
                </Label>
                <Input
                  id="linkText"
                  {...register('linkText', { maxLength: 50 })}
                  placeholder={t`Learn more`}
                />
              </div>
            </div>

            {/* Schedule */}
            <div className="space-y-4">
              <Label>
                <Trans>Schedule</Trans>
              </Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="activeFrom" className="text-xs text-muted-foreground">
                    <Trans>Start showing</Trans>
                  </Label>
                  <Input id="activeFrom" type="datetime-local" {...register('activeFrom')} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="activeUntil" className="text-xs text-muted-foreground">
                    <Trans>Stop showing</Trans>
                  </Label>
                  <Input id="activeUntil" type="datetime-local" {...register('activeUntil')} />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                <Trans>Leave start empty to save as draft. Leave end empty for no expiry.</Trans>
              </p>
            </div>

            {/* Target Platforms */}
            <div className="space-y-3">
              <Label>
                <Trans>Target Platforms</Trans>
              </Label>
              <div className="flex gap-2">
                {ANNOUNCEMENT_PLATFORMS.map((platform) => (
                  <button
                    key={platform}
                    type="button"
                    onClick={() => togglePlatform(platform)}
                    className={cn(
                      'flex-1 rounded-lg border py-2 px-3 text-sm font-medium transition-colors',
                      watchedValues.targetPlatforms?.includes(platform)
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-muted/30 border-border hover:bg-muted/50'
                    )}
                  >
                    {PLATFORM_LABELS[platform]}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                <Trans>Leave all unchecked to show on all platforms.</Trans>
              </p>
            </div>

            {/* Dismissible */}
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <Label htmlFor="dismissible" className="font-medium">
                  <Trans>Allow dismiss</Trans>
                </Label>
                <p className="text-sm text-muted-foreground">
                  <Trans>Users can hide this announcement</Trans>
                </p>
              </div>
              <Switch
                id="dismissible"
                checked={watchedValues.dismissible}
                onCheckedChange={(checked) =>
                  setValue('dismissible', checked, { shouldDirty: true })
                }
              />
            </div>

            {/* Stats (if editing) */}
            {isEditing && stats && (
              <div className="rounded-lg bg-muted/30 p-4">
                <h4 className="text-sm font-medium mb-3">
                  <Trans>Statistics</Trans>
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-2xl font-semibold">{stats.dismissals}</p>
                    <p className="text-muted-foreground">
                      <Trans>Dismissals</Trans>
                    </p>
                  </div>
                  <div>
                    <p className="text-2xl font-semibold">{stats.dismissalRate.toFixed(1)}%</p>
                    <p className="text-muted-foreground">
                      <Trans>Dismissal Rate</Trans>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-3 pt-4 border-t">
              {isEditing && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDeleteDialog(true)}
                  className="text-destructive hover:bg-destructive/10"
                >
                  <TrashIcon className="h-4 w-4 mr-2" />
                  <Trans>Delete</Trans>
                </Button>
              )}
              <div className="flex-1" />
              <Button type="button" variant="outline" onClick={onClose}>
                <Trans>Cancel</Trans>
              </Button>
              <Button type="submit" disabled={isPending || (!isDirty && isEditing)}>
                {isPending ? (
                  <Trans>Saving...</Trans>
                ) : isEditing ? (
                  <Trans>Save Changes</Trans>
                ) : (
                  <Trans>Create Announcement</Trans>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && announcement && (
        <Dialog open onOpenChange={() => setShowDeleteDialog(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                <Trans>Delete Announcement</Trans>
              </DialogTitle>
              <DialogDescription>
                <Trans>
                  Are you sure you want to delete &quot;{announcement.title}&quot;? This action
                  cannot be undone.
                </Trans>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                <Trans>Cancel</Trans>
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? <Trans>Deleting...</Trans> : <Trans>Delete</Trans>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
